import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill/dist';
import { debounce, identity, throttle } from 'lodash-es';
import { contentLoaded } from './utils/document-promises';
import { addTagsToEmailSignupForms, safeUpdateHtmlNodeAttributes } from './utils/dom';
import { getLocationInfo, isInGeotarget } from './utils/location';
import { InitialUrlSearchParams } from './utils/environment';
import {
  checkIfCustomer,
  IS_NATIVE_APP_CLIENT,
  tryCatchWrapper,
  poll,
  recordCustomEventToNewRelic,
} from './utils/helpers';
import { autorun } from 'mobx';
import { ShippingCountryState } from './utils/shipping';
import {
  fireGA4CustomerAuthorizationEvent,
  fireGA4LinkClickTracking,
  fireGA4MenuLinkTracking,
  fireGA4pageViewEventForMobileApps,
  pushBloomreachPageViewFromPathname,
} from './utils/gtm-data-layer';
import { getCookie, setCookie } from './utils/cookies';
import { globalWindow, IS_PRESUMABLY_EXISTING_CUSTOMER, IS_USER_JUST_AUTHORIZED } from './utils/constants';
import { openSignInModal } from './templates/_shared/ui-components/SignInModal/utils';
import React from 'react';
import { currentPathEquals, prependLocaleToPath, setupAutomaticLinkLocalization } from './utils/localization';
import { setupAuthHandler } from './utils/auth-handler';
import { fireFSMenuLinkTracking, identifyUserInFullStory } from './utils/full-story';
import { handleLogout } from './global';
import { synchronizeAloAuthToken } from './utils/multipass-auth-cookie';
import { observeBranchDeeplinkTargets } from './utils/branch-deeplinks';
import { removeRsvpTokens, getRsvpTokens, setRsvpTokens, RSVP_COMMUNITY_TAG } from 'src/scripts/utils/rsvp-events';
import { initSierraLiveChat } from './utils/sierra';

polyfillCountryFlagEmojis('Twemoji Country Flags', window.twemojiCountryFlagsUrl);

// ? can't tell if this format is any better, may elect change it back or change it in the future.
const startupScripts = async () => {
  countPageVisitsAndStoreToCookie();
  const locationInfo = await getLocationInfo();
  await contentLoaded;

  // Calling the Loop on Store init

  poll(() => window.LoopOnstore, 5000, 500).then((isLoopScriptLoaded) => {
    if (isLoopScriptLoaded) {
      window.LoopOnstore.init({
        key: process.env.REACT_APP_LOOP_API_KEY,
        attach: '.loop-button-attach, .atc-checkout-button, .btn-checkout-fixed, .btn__checkout, .btn__checkout--cart',
      });
    }
  });
  adjustMainNavPosition(); // Check if Uni banner sticky and adjust the postion of nav accordingly
  conditionallyInjectBranchSDK();
  await conditionallyUpdateAccessLink();

  tryCatchWrapper(addTagsToEmailSignupForms);
  tryCatchWrapper(initSierraLiveChat);
  handleNavigationGA4tracking();
  handleLinkClickGA4Tracking();
  handleCustomerAuthorizationGA4tracking();

  if (IS_NATIVE_APP_CLIENT) {
    fireGA4pageViewEventForMobileApps();
  } else {
    identifyUserInFullStory(); // For FS POC
    pushBloomreachPageViewFromPathname(window.location.pathname);
  }

  if (currentPathEquals('/account')) {
    tryCatchWrapper(handleLogout);
  }
  // Email modal geo-targeted content update
  try {
    //@ts-expect-error
    const { isGeo, title, subtitle, countryCodes } = window?.getShopifySettingsEmailModal();
    const countryCodesArray = countryCodes
      .split(',')
      .filter(identity) // Removes any empty ones caused by multiple/trailing commas
      //@ts-expect-error
      .map((countryCode) => countryCode.trim().toUpperCase());

    if (isGeo && countryCodesArray.includes(locationInfo.country_code)) {
      safeUpdateHtmlNodeAttributes('.modal-title__geo', {
        innerHTML: title,
      });
      safeUpdateHtmlNodeAttributes('.modal-subtitle__geo', {
        innerHTML: subtitle,
      });
    }
  } catch (e) {
    console.log(e);
  }
  conditionallyRenderLoyaltyFAQContent(); // [REVISIT] Why it's here? We should call this fn only on Access page
  handleYogaUIinputStyling();
  if (
    !globalWindow.location.pathname.includes('alo-gives') &&
    globalWindow.location.pathname !== prependLocaleToPath('/collections')
  ) {
    conditionallyShowEmailModal();
  }

  import('./utils/early-logout-detection').then(({ detectAndReportEarlyLogout }) => {
    detectAndReportEarlyLogout();
  });

  const hasRsvpTokens = () => {
    const { pendingRsvpUrl, returnUrl } = getRsvpTokens();
    return !!pendingRsvpUrl || !!returnUrl;
  };

  if (currentPathEquals('/pages/alo-events')) {
    handleRsvpGating();

    // Fix bfcache issue for alo-events: when user signs up and redirects to Luma, then clicks back
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    });
  } else if (hasRsvpTokens()) {
    removeRsvpTokens();
  }
};

startupScripts();

const conditionallyInjectBranchSDK = () => {
  try {
    const { branchSDKenabled, branchDeeplinkAttachmentsEnabled, branchAppStoreUrl } = window.getSiteSettings();
    const BRANCH_SDK_KEY = process.env.REACT_APP_BRANCH_SDK_KEY;
    if (branchSDKenabled && BRANCH_SDK_KEY) {
      import('branch-sdk').then((module) => {
        module.default.init(BRANCH_SDK_KEY, {}, function (error, data) {
          if (error) {
            console.log('Error initiating Branch SDK', error);
            recordCustomEventToNewRelic('branch_sdk_error', {
              timestamp: Date.now(),
              message: `Error initiating Branch SDK. ${error}`,
            });
          }
          if (branchDeeplinkAttachmentsEnabled && branchAppStoreUrl) {
            observeBranchDeeplinkTargets(branchAppStoreUrl);
          }
        });
      });
    }
  } catch (error) {
    console.log('Error initiating Branch SDK: ', error);
    recordCustomEventToNewRelic('branch_sdk_error', {
      timestamp: Date.now(),
      message: `Error initiating Branch SDK. ${error}`,
    });
  }
};

// Show Duties and Taxes disclaimer for INTL users
const toggleDutiesDisclaimerInFooter = (countryCode: string | null) => {
  const disclaimerNode = document.querySelector('.duties-disclaimer');
  const copyright = document.querySelector('.copyright');
  if (countryCode !== 'US' && disclaimerNode) {
    disclaimerNode.style.display = 'block';
    copyright?.style.setProperty('--copyright-margin', '0');
  } else {
    if (disclaimerNode) disclaimerNode.style.display = 'none';
    copyright?.style.setProperty('--copyright-margin', '25px');
  }
};

autorun(() => {
  toggleDutiesDisclaimerInFooter(ShippingCountryState.countryCode);
});

const handleNavigationGA4tracking = () => {
  const navLinks = Array.from(document.querySelectorAll('[data-nav]'));
  navLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      const link = e.target as HTMLAnchorElement;
      if (link) {
        fireGA4MenuLinkTracking(link);
        //!![REVIST] For FS POC
        fireFSMenuLinkTracking(link);
      }
    });
  });
};

const handleLinkClickGA4Tracking = () => {
  const navLinks = document.querySelectorAll('[data-gtm="link_click"]');
  navLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      const link = e.currentTarget as HTMLAnchorElement;
      if (link) {
        fireGA4LinkClickTracking(link);
      }
    });
  });
};

const handleCustomerAuthorizationGA4tracking = () => {
  if (checkIfCustomer() && IS_USER_JUST_AUTHORIZED) {
    const urlParams = InitialUrlSearchParams;
    const event = urlParams.get('auth_event') as 'login' | 'sign_up' | null;
    const customerInfo = window.getCustomerInfo && window.getCustomerInfo();

    const mode = (() => {
      if (urlParams.get('social_login') === 'true') return 'social login';
      if (urlParams.get('auth_mode') === 'faceid') return 'face id';
      return 'email';
    })();
    const options = {
      event,
      customer_id: customerInfo?.customerId.toString() || null,
      loyalty_tier: customerInfo?.loyalty_tier || null,
      method: urlParams.get('is_modal') === 'true' ? 'Sign In/Sign Up Modal' : 'Embedded Auth Form',
      is_social_login: urlParams.get('social_login') || 'false',
      ...(mode && { mode }),
    };

    if (event) fireGA4CustomerAuthorizationEvent(options);
  }
};

const adjustMainNavPosition = () => {
  const uniBannerWrapper = document.getElementById('uni-banner-wrapper');
  const mainNav = document.getElementById('shopify-section-main-nav');
  const isCheckOut = document.getElementById('checkout');
  const isUniBannerSticky = uniBannerWrapper?.classList.contains('sticky-uni-banner');
  if (mainNav && isUniBannerSticky && !isCheckOut) {
    // update the main nav top to look consistent with the sticky universal banner
    mainNav.style.top = `${uniBannerWrapper?.offsetHeight}px`;
  }
};

window.addEventListener('resize', adjustMainNavPosition);

// Email modal logic
function conditionallyShowEmailModal() {
  if (IS_NATIVE_APP_CLIENT) return null;

  const mountNode = document.querySelector('[react-render-target="email-modal-body"]');
  const visitedPageNumber = Number(getCookie('modVisited'));

  const shouldShowEmailModal = visitedPageNumber === 2 && getCookie('modal-email-footer') === '';

  if (shouldShowEmailModal) {
    mountNode
      ? import('./templates/product/components/EmailModal/renderEmailModal').then((module) =>
          module.renderEmailModal(mountNode)
        )
      : console.warn('missing render target: EmailModal');
  }
}

// Loyalty FAQ Builder Content
async function conditionallyRenderLoyaltyFAQContent() {
  const loyaltySettings = await GWPandLoyaltyGeoSettings();
  const shouldRenderLoyalty =
    loyaltySettings.shouldRenderLoyaltyForDomestic || loyaltySettings.shouldRenderLoyaltyWidgetForINTL;

  const mountNode = document.querySelector('[react-render-target="access-faqs"]');
  if (shouldRenderLoyalty) {
    mountNode
      ? import('./templates/page/components/renderLoyaltyBuilderFAQContent').then((module) =>
          module.LoyaltyBuilderFAQDropzone(mountNode)
        )
      : console.warn('missing render target: LoyaltyFAQs');
  }
}

function countPageVisitsAndStoreToCookie() {
  let modVisited = Number(getCookie('modVisited'));
  modVisited += 1;
  setCookie('modVisited', modVisited, 7);
}

const handleYogaUIinputStyling = () => {
  const yogaUIinputs: HTMLInputElement[] = Array.from(document.querySelectorAll('.yoga-ui-input:not(.react-input)'));

  if (yogaUIinputs.length > 0) {
    yogaUIinputs.forEach((input) => {
      input.value !== '' && input.classList.add('has-value');
      input.addEventListener('input', (e: Event) => {
        const input = e.target as HTMLInputElement;
        input && input.value !== '' ? input.classList.add('has-value') : input.classList.remove('has-value');
      });
    });
  }
};

async function conditionallyUpdateAccessLink() {
  const rewardsMainNavLink: HTMLAnchorElement | null = document.querySelector('li#rewards a');
  const rewardsAccountLinks: NodeListOf<HTMLAnchorElement> | null = document.querySelectorAll('li#alo-access a');

  if (rewardsMainNavLink) setUrlForInternational(rewardsMainNavLink);
  if (rewardsAccountLinks) rewardsAccountLinks.forEach((link) => setUrlForInternational(link));
}

async function setUrlForInternational(anchorElement: HTMLAnchorElement) {
  const loyaltySettings = await GWPandLoyaltyGeoSettings();
  if (loyaltySettings.shouldRenderLoyaltyWidgetForINTL) anchorElement.href = prependLocaleToPath('/pages/in-access');
}

export const GWPandLoyaltyGeoSettings = async () => {
  const isInUSgeo = await isInGeotarget({ countries: ['US'] });
  const isInUS = ShippingCountryState.countryCode ? ShippingCountryState.countryCode === 'US' : isInUSgeo;
  const shouldRenderLoyaltyForDomestic = (window.loyalty_settings?.isLoyaltyForDomesticEnabled && isInUS) || false;
  const shouldRenderLoyaltyWidgetForINTL = (window.loyalty_settings?.isLoyaltyForINTLEnabled && !isInUS) || false;
  const shouldRenderGWPforINTL = (window.gwp_settings?.isGWPenabled && !isInUS) || false;
  const shouldHideWidget = window.loyalty_settings?.isLLwidgetHidden || false;
  const redeemButtonText = window.loyalty_settings?.redeemButtonText;
  const alreadyRedeemedButtonText = window.loyalty_settings?.alreadyRedeemedButtonText;
  const redeemingButtonText = window.loyalty_settings?.redeemingButtonText;
  const redeemAgainButtonText = window.loyalty_settings?.redeemAgainButtonText;
  const customSubtitleBeforePoints = window.loyalty_settings?.customSubtitleBeforePoints;
  const accessPageWidgetDescription = window.loyalty_settings?.accessPageWidgetDescription;
  const accessPageWidgetTitle = window.loyalty_settings?.accessPageWidgetTitle;
  return {
    shouldRenderLoyaltyForDomestic,
    shouldRenderGWPforINTL,
    shouldHideWidget,
    shouldRenderLoyaltyWidgetForINTL,
    redeemButtonText,
    redeemAgainButtonText,
    alreadyRedeemedButtonText,
    redeemingButtonText,
    customSubtitleBeforePoints,
    accessPageWidgetDescription,
    accessPageWidgetTitle,
  };
};

export function attachSignInModalOpenerToElements() {
  document.querySelectorAll('a[data-logged-out]').forEach((element) => {
    element.addEventListener('click', (event) => {
      conditionallyOpenSignInModalOnClick(event);
    });
  });
}

export const conditionallyOpenSignInModalOnClick = (event: MouseEvent | React.MouseEvent) => {
  if (IS_NATIVE_APP_CLIENT) {
    event.preventDefault();
    window.location.href = prependLocaleToPath(`/account/login?newUser=${!IS_PRESUMABLY_EXISTING_CUSTOMER}`);
  } else {
    event.preventDefault();
    let returnUrl = window.location.pathname + window.location.search;
    if ((event?.currentTarget as Element).classList?.contains('main-navbar__icon--wishlist')) {
      returnUrl = prependLocaleToPath('/account?view=wishlist');
    }
    if (returnUrl.match(/\/account\/login/)) {
      returnUrl = prependLocaleToPath('/account?auth_success=1');
    }

    openSignInModal({ returnUrl, tab: IS_PRESUMABLY_EXISTING_CUSTOMER ? 'sign-in' : 'create-account' });
  }
};

window.conditionallyOpenSignInModalOnClick = conditionallyOpenSignInModalOnClick;

function handleRsvpGating() {
  if (checkIfCustomer()) {
    const { pendingRsvpUrl, returnUrl } = getRsvpTokens();
    if (pendingRsvpUrl && returnUrl === window.location.pathname) {
      removeRsvpTokens();
      window.location.href = appendUserEmailToLumaUrl(pendingRsvpUrl);
      return;
    }
  }

  document.addEventListener(
    'click',
    (event: Event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest<HTMLAnchorElement>('a[href*="luma.com"]');
      if (!link) return;

      const mouseEvent = event as MouseEvent;
      const isModifiedClick =
        mouseEvent.ctrlKey || mouseEvent.metaKey || mouseEvent.shiftKey || mouseEvent.button !== 0;
      const hasTarget = link.target && link.target !== '_self';

      if (isModifiedClick || hasTarget) return;

      event.preventDefault();
      if (checkIfCustomer()) {
        window.location.href = appendUserEmailToLumaUrl(link.href);
      } else {
        setRsvpTokens(link.href);

        if (IS_NATIVE_APP_CLIENT) {
          window.location.href = prependLocaleToPath(
            `/account/login?newUser=${!IS_PRESUMABLY_EXISTING_CUSTOMER}&tag=${RSVP_COMMUNITY_TAG}`
          );
        } else {
          // On web, open sign-in modal
          openSignInModal({
            returnUrl: `${window.location.pathname}${window.location.search}`,
            tab: IS_PRESUMABLY_EXISTING_CUSTOMER ? 'sign-in' : 'create-account',
          });
        }
      }
    },
    true
  );
}

function appendUserEmailToLumaUrl(url: string) {
  const customerEmail = window.getCustomerInfo?.().email;
  const finalUrl = customerEmail
    ? `${url}${url.includes('?') ? '&' : '?'}email=${encodeURIComponent(customerEmail)}`
    : url;
  return finalUrl;
}

// Featured collection
function handleSlider(sectionId: string) {
  if (document.documentElement.clientWidth >= 768) {
    const section = document.querySelector(`#shopify-section-${sectionId} .product--row`);
    const arrowRight = document.querySelector(`#shopify-section-${sectionId} .arrow--right`);
    const arrowLeft = document.querySelector(`#shopify-section-${sectionId} .arrow--left`);
    const products = [...document.querySelectorAll(`#shopify-section-${sectionId} .product-container`)];
    const measures = products.map((product) => product.getBoundingClientRect().width);
    const rowWidth = measures.reduce((a, b) => a + b); // Get the real row width depending on the counts of the products
    const sectionScrollPositionFromLeft = section?.scrollLeft; // Section's horizontal scroll position from left
    const coordX = 5; // Define the scroll X-coordinate in px where arrows appear/hide
    const endOfSection = rowWidth - document.documentElement.clientWidth - 50;

    if (sectionScrollPositionFromLeft && sectionScrollPositionFromLeft > coordX) {
      if (arrowLeft) arrowLeft.style.display = 'block';
    } else if (sectionScrollPositionFromLeft && sectionScrollPositionFromLeft <= coordX) {
      if (arrowLeft) arrowLeft.style.display = 'none';
      if (arrowRight) arrowRight.style.display = 'block';
    }

    if (sectionScrollPositionFromLeft && sectionScrollPositionFromLeft >= endOfSection) {
      if (arrowRight) arrowRight.style.display = 'none';
    } else {
      if (arrowRight) arrowRight.style.display = 'block';
    }
  }
}

export const throttledHandleSlider = throttle((sectionId) => {
  handleSlider(sectionId);
}, 150);

export const debouncedPlaceArrows = debounce((sectionId, width) => {
  placeArrows(sectionId, width);
}, 150);

export function placeArrows(sectionId: string, width: number = 0) {
  if (document.documentElement.clientWidth >= 768 && document.documentElement.clientWidth !== width) {
    const arrowRight = document.querySelector<HTMLDivElement>(`#shopify-section-${sectionId} div.arrow--right`);
    const arrowLeft = document.querySelector<HTMLDivElement>(`#shopify-section-${sectionId} div.arrow--left`);
    const images = [
      ...document.querySelectorAll(`#shopify-section-${sectionId} .card-image img`),
    ] as HTMLImageElement[];
    if (!images[0] || !arrowRight || !arrowLeft) return;
    const arrowHeight = arrowRight.clientHeight !== 0 ? arrowRight.clientHeight : arrowLeft.clientHeight;
    const imageHeight = images[0].height;
    arrowLeft.style.top = `${imageHeight / 2 - arrowHeight / 2}px`;
    arrowRight.style.top = `${imageHeight / 2 - arrowHeight / 2}px`;
  }
}

globalWindow.slideOneScreen = function (direction: string, sectionId: string) {
  const section = document.querySelector(`#shopify-section-${sectionId} .product--row`);
  if (direction === 'right') {
    section?.scrollBy({
      left: section.getBoundingClientRect().width,
      behavior: 'smooth',
    });
  } else {
    section?.scrollBy({
      left: -section.getBoundingClientRect().width,
      behavior: 'smooth',
    });
  }
};

// Shared section "hp-featured-collection"
globalWindow.runFeatCollectionSectionScripts = function ({ sectionId }: { sectionId: string }) {
  if (!sectionId) {
    return;
  }
  const section = document.querySelector(`#shopify-section-${sectionId} .product--row`);
  section?.addEventListener('scroll', () => {
    throttledHandleSlider(sectionId);
  });
  placeArrows(sectionId);
  let width = document.documentElement.clientWidth;
  window.addEventListener('resize', () => {
    debouncedPlaceArrows(sectionId, width);
    width = document.documentElement.clientWidth;
  });
};

window.addEventListener('DOMContentLoaded', () => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(synchronizeAloAuthToken, { timeout: 5000 });
  } else {
    setTimeout(synchronizeAloAuthToken, 0);
  }
});
setupAutomaticLinkLocalization();
setupAuthHandler();
